/**
 * Request/response schemas for the physical therapy & rehab specialty module (`BACKLOG.md` M6). Same
 * reasoning as `dermatologyRequests.ts`/`aestheticsRequests.ts`: these live in `shared/` so the
 * server validates against the exact schema the web app infers its types from, every object schema
 * is strict so an unrecognised key is a rejection rather than silently ignored, and no server-owned
 * value (an id, `orgId`, a timestamp, `providerId`) appears in any request schema — the route layer
 * supplies those from the session, never from the body.
 */

import { z } from 'zod';

import {
  calendarDateSchema,
  confidentialityReasonsInputSchema,
  restrictionReasonSchema,
} from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { BODY_MAP_REGIONS, BODY_MAP_SIDES } from './bodyMapping.ts';
import { OUTCOME_INSTRUMENTS } from './physicalTherapy/outcomeInstruments.ts';

export const MAX_CPT_CODE_LENGTH = 10;
export const MAX_EXERCISE_NAME_LENGTH = 150;
export const MAX_RESISTANCE_LENGTH = 100;
export const MAX_INSTRUCTIONS_LENGTH = 1000;
export const MAX_VISIT_NOTES_LENGTH = 2000;
export const MAX_PROGRAM_NAME_LENGTH = 150;
export const MAX_DISCHARGE_SUMMARY_LENGTH = 2000;

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) => trimmed(max).optional();
const positiveInt = () => z.number().int().positive();

/** A non-negative decimal string, matching `aestheticsInjectableAdministrations.units`'s own
 * open-but-checked convention for a quantity that needs real arithmetic done against it elsewhere. */
const decimalScoreSchema = z
  .string()
  .regex(/^[0-9]+(\.[0-9]{1,2})?$/, 'must be a non-negative decimal number');

// --- Plans of care --------------------------------------------------------------------------------

export const createPlanOfCareRequestSchema = z
  .object({
    evalDate: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreatePlanOfCareRequest = z.infer<typeof createPlanOfCareRequestSchema>;

/**
 * Every lifecycle field is optional so a single PATCH can advance exactly one step (establish the
 * POC, countersign it, certify a period, log a progress report, discharge) without restating the
 * others — the same "at least one field" shape `dermatologyRequests.ts`'s own
 * `updateDermatologyProfileRequestSchema` uses, for the identical reason: an empty patch is rejected
 * rather than silently accepted as a no-op.
 */
export const updatePlanOfCareRequestSchema = z
  .object({
    pocEstablishedDate: calendarDateSchema.optional(),
    physicianSignedDate: calendarDateSchema.optional(),
    certificationPeriodStart: calendarDateSchema.optional(),
    certificationPeriodEnd: calendarDateSchema.optional(),
    lastProgressReportDate: calendarDateSchema.optional(),
    dischargeDate: calendarDateSchema.optional(),
    dischargeSummary: optionalTrimmed(MAX_DISCHARGE_SUMMARY_LENGTH),
    confidentialityReasons: z.array(restrictionReasonSchema).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.pocEstablishedDate !== undefined ||
      value.physicianSignedDate !== undefined ||
      value.certificationPeriodStart !== undefined ||
      value.certificationPeriodEnd !== undefined ||
      value.lastProgressReportDate !== undefined ||
      value.dischargeDate !== undefined ||
      value.dischargeSummary !== undefined,
    { message: 'at least one lifecycle field is required' },
  );
export type UpdatePlanOfCareRequest = z.infer<typeof updatePlanOfCareRequestSchema>;

export const dueStateSchema = z.enum([
  'not_applicable',
  'on_track',
  'due_soon',
  'overdue',
  'complete',
]);

export const planOfCareStatusResponseSchema = z.object({
  overallState: z.enum(['evaluation', 'pending_signature', 'active', 'recert_due', 'discharged']),
  signatureState: dueStateSchema,
  signatureDueDate: calendarDateSchema.nullable(),
  certificationState: dueStateSchema,
  certificationDueDate: calendarDateSchema.nullable(),
  progressReportState: dueStateSchema,
  progressReportDueDate: calendarDateSchema.nullable(),
});

export const planOfCareResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  evalDate: calendarDateSchema,
  pocEstablishedDate: calendarDateSchema.nullable(),
  physicianSignedDate: calendarDateSchema.nullable(),
  certificationPeriodStart: calendarDateSchema.nullable(),
  certificationPeriodEnd: calendarDateSchema.nullable(),
  lastProgressReportDate: calendarDateSchema.nullable(),
  dischargeDate: calendarDateSchema.nullable(),
  dischargeSummary: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  status: planOfCareStatusResponseSchema,
});
export type PlanOfCareResponse = z.infer<typeof planOfCareResponseSchema>;

export const planOfCareEnvelopeSchema = z.object({ planOfCare: planOfCareResponseSchema });
export const planOfCareListEnvelopeSchema = z.object({
  plansOfCare: z.array(planOfCareResponseSchema),
});

// --- Visits & the 8-minute-rule billing engine -----------------------------------------------------

export const timedUnitInputSchema = z
  .object({
    cptCode: trimmed(MAX_CPT_CODE_LENGTH),
    minutes: positiveInt(),
  })
  .strict();

export const createVisitRequestSchema = z
  .object({
    planOfCareId: z.uuid(),
    visitDate: calendarDateSchema,
    bodyMapRegion: z.enum(BODY_MAP_REGIONS).optional(),
    bodyMapSide: z.enum(BODY_MAP_SIDES).optional(),
    notes: optionalTrimmed(MAX_VISIT_NOTES_LENGTH),
    /** At least one timed code — a visit with zero timed minutes documented is not a billable visit
     * this engine can allocate anything for. `superRefine` below also rejects a duplicate CPT code at
     * the request boundary, ahead of `allocateEightMinuteRuleUnits`'s own identical refusal, so the
     * caller gets a plain 400 rather than a service-layer surprise. */
    timedUnits: z.array(timedUnitInputSchema).min(1),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const [index, entry] of value.timedUnits.entries()) {
      if (seen.has(entry.cptCode)) {
        ctx.addIssue({
          code: 'custom',
          message: 'duplicate CPT code in timedUnits',
          path: ['timedUnits', index, 'cptCode'],
        });
      }
      seen.add(entry.cptCode);
    }
  });
export type CreateVisitRequest = z.infer<typeof createVisitRequestSchema>;

export const billedUnitLineResponseSchema = z.object({
  cptCode: z.string(),
  units: z.number().int().nonnegative(),
});

export const visitResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  planOfCareId: z.uuid(),
  visitDate: calendarDateSchema,
  providerId: z.uuid(),
  bodyMapRegion: z.string().nullable(),
  bodyMapSide: z.string().nullable(),
  notes: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  timedUnits: z.array(z.object({ cptCode: z.string(), minutes: z.number().int().positive() })),
  /** Computed at read time from `timedUnits` via `allocateEightMinuteRuleUnits` — never stored, the
   * same "compute at read time, never store the coded output" convention
   * `dermatology_destruction_procedures`'s CPT lines already establish in this codebase, so a future
   * correction to the UNREVIEWED thresholds in `eightMinuteRule.ts` cannot leave a stale billed line
   * behind on an old visit. */
  totalMinutes: z.number().int().nonnegative(),
  totalUnits: z.number().int().nonnegative(),
  billedLines: z.array(billedUnitLineResponseSchema),
  billingRefused: z.boolean(),
});
export type VisitResponse = z.infer<typeof visitResponseSchema>;

export const visitEnvelopeSchema = z.object({ visit: visitResponseSchema });
export const visitListEnvelopeSchema = z.object({ visits: z.array(visitResponseSchema) });

// --- Exercise flowsheets ----------------------------------------------------------------------------

export const addExerciseFlowsheetEntryRequestSchema = z
  .object({
    exerciseName: trimmed(MAX_EXERCISE_NAME_LENGTH),
    sets: positiveInt().optional(),
    reps: positiveInt().optional(),
    resistance: optionalTrimmed(MAX_RESISTANCE_LENGTH),
    notes: optionalTrimmed(MAX_VISIT_NOTES_LENGTH),
  })
  .strict();
export type AddExerciseFlowsheetEntryRequest = z.infer<
  typeof addExerciseFlowsheetEntryRequestSchema
>;

export const exerciseFlowsheetEntryResponseSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  exerciseName: z.string(),
  sets: z.number().int().nullable(),
  reps: z.number().int().nullable(),
  resistance: z.string().nullable(),
  notes: z.string().nullable(),
});
export type ExerciseFlowsheetEntryResponse = z.infer<typeof exerciseFlowsheetEntryResponseSchema>;

export const exerciseFlowsheetEntryEnvelopeSchema = z.object({
  entry: exerciseFlowsheetEntryResponseSchema,
});

// --- Home exercise programs ------------------------------------------------------------------------

export const homeExerciseProgramItemInputSchema = z
  .object({
    exerciseName: trimmed(MAX_EXERCISE_NAME_LENGTH),
    instructions: optionalTrimmed(MAX_INSTRUCTIONS_LENGTH),
    sets: positiveInt().optional(),
    reps: positiveInt().optional(),
    frequencyPerWeek: positiveInt().optional(),
  })
  .strict();
export type HomeExerciseProgramItemInput = z.infer<typeof homeExerciseProgramItemInputSchema>;

export const createHomeExerciseProgramRequestSchema = z
  .object({
    name: trimmed(MAX_PROGRAM_NAME_LENGTH),
    prescribedOn: calendarDateSchema,
    items: z.array(homeExerciseProgramItemInputSchema).min(1),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateHomeExerciseProgramRequest = z.infer<
  typeof createHomeExerciseProgramRequestSchema
>;

export const addHomeExerciseProgramItemRequestSchema = homeExerciseProgramItemInputSchema;
export type AddHomeExerciseProgramItemRequest = z.infer<
  typeof addHomeExerciseProgramItemRequestSchema
>;

export const homeExerciseProgramItemResponseSchema = z.object({
  id: z.uuid(),
  programId: z.uuid(),
  exerciseName: z.string(),
  instructions: z.string().nullable(),
  sets: z.number().int().nullable(),
  reps: z.number().int().nullable(),
  frequencyPerWeek: z.number().int().nullable(),
});

export const homeExerciseProgramResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  name: z.string(),
  prescribedOn: calendarDateSchema,
  active: z.boolean(),
  confidentialityReasons: z.array(z.string()),
  items: z.array(homeExerciseProgramItemResponseSchema),
});
export type HomeExerciseProgramResponse = z.infer<typeof homeExerciseProgramResponseSchema>;

export const homeExerciseProgramEnvelopeSchema = z.object({
  program: homeExerciseProgramResponseSchema,
});
export const homeExerciseProgramListEnvelopeSchema = z.object({
  programs: z.array(homeExerciseProgramResponseSchema),
});
export const homeExerciseProgramItemEnvelopeSchema = z.object({
  item: homeExerciseProgramItemResponseSchema,
});

// --- Scored outcome instruments ---------------------------------------------------------------------

export const recordOutcomeMeasureRequestSchema = z
  .object({
    instrument: z.enum(OUTCOME_INSTRUMENTS),
    rawScore: decimalScoreSchema,
    scoredOn: calendarDateSchema,
    visitId: z.uuid().optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type RecordOutcomeMeasureRequest = z.infer<typeof recordOutcomeMeasureRequestSchema>;

export const outcomeMeasureResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid().nullable(),
  instrument: z.string(),
  rawScore: z.string(),
  scoredOn: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  /** Computed at read time via `toPercentDisability` — `null` when the stored raw score falls
   * outside that instrument's own valid range (should not happen through this module's own write
   * path, since the range is enforced there too, but a `null` here rather than a thrown error keeps
   * a read robust against a row written by some other path). */
  percentDisability: z.number().nullable(),
});
export type OutcomeMeasureResponse = z.infer<typeof outcomeMeasureResponseSchema>;

export const outcomeMeasureEnvelopeSchema = z.object({ measure: outcomeMeasureResponseSchema });
export const outcomeMeasureListEnvelopeSchema = z.object({
  measures: z.array(outcomeMeasureResponseSchema),
});

// --- Failure vocabulary -----------------------------------------------------------------------------

export const PHYSICAL_THERAPY_ERROR_CODES = [...CLINICAL_ERROR_CODES, 'not_entitled'] as const;
export type PhysicalTherapyErrorCode = (typeof PHYSICAL_THERAPY_ERROR_CODES)[number];
